var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { IRampProviderType, RAMP_SOCKET_TIMEOUT, SELL_ORDER_DISPLAY_STATUS } from './constants/index.js';
import { AlchemyPayProvider, TransakProvider } from './provider/index.js';
import { AlchemyPayRampService, RampService } from './service/index.js';
import { RampSignalr } from './signalr/index.js';
import { randomId } from '@portkey/utils';
import { FetchRequest } from '@portkey/request';
import { RampConfig } from './config/index.js';
export class BaseRamp {
    constructor() {
        this.config = new RampConfig();
        this.fetchRequest = new FetchRequest(this.config.requestConfig);
        this.service = new RampService({
            fetchRequest: this.fetchRequest,
        });
        this.rampSignalr = new RampSignalr();
        this.providerMap = {};
    }
    setConfig(option) {
        this.config.setConfig(option);
    }
    setProvider(provider) {
        this.providerMap[provider.providerInfo.key] = provider;
    }
    getProvider(key) {
        return this.providerMap[key];
    }
    removeProvider(key) {
        const provider = this.providerMap[key];
        delete this.providerMap[key];
        return provider;
    }
    clearProvider() {
        this.providerMap = {};
    }
    transferCrypto(orderId, generateTransaction) {
        return __awaiter(this, void 0, void 0, function* () {
            const clientId = randomId();
            try {
                yield this.rampSignalr.doOpen({
                    url: this.config.requestConfig.socketUrl || `${this.config.requestConfig.baseURL || ''}/communication`,
                    clientId,
                });
            }
            catch (error) {
                console.log('rampSignalr.doOpen error: ', error);
            }
            let timer = undefined;
            const timerPromise = new Promise(resolve => {
                timer = setTimeout(() => {
                    resolve('timeout');
                }, RAMP_SOCKET_TIMEOUT);
            });
            let signalFinishPromiseResolve;
            const signalFinishPromise = new Promise(resolve => {
                signalFinishPromiseResolve = resolve;
            });
            let isTransferred = false;
            const { remove: removeAchTx } = this.rampSignalr.onRampOrderChanged((data) => __awaiter(this, void 0, void 0, function* () {
                if (data.displayStatus === SELL_ORDER_DISPLAY_STATUS.TRANSFERRED) {
                    signalFinishPromiseResolve(data);
                    return;
                }
                if (data.displayStatus !== SELL_ORDER_DISPLAY_STATUS.CREATED) {
                    return;
                }
                try {
                    const result = yield generateTransaction(data);
                    yield this.service.sendSellTransaction({
                        merchantName: data.merchantName,
                        orderId,
                        rawTransaction: result.rawTransaction,
                        signature: result.signature,
                        publicKey: result.publicKey,
                    });
                    isTransferred = true;
                }
                catch (e) {
                    signalFinishPromiseResolve(null);
                    return;
                }
            }));
            yield this.rampSignalr.requestRampOrderStatus(clientId, orderId);
            const signalrSellResult = yield Promise.race([timerPromise, signalFinishPromise]);
            removeAchTx();
            this.rampSignalr.stop();
            if (timer) {
                clearTimeout(timer);
                timer = undefined;
            }
            if (signalrSellResult === null)
                throw new Error('Transaction failed.');
            if (signalrSellResult === 'timeout') {
                if (!isTransferred)
                    throw new Error('Transaction failed.');
                throw {
                    code: 'TIMEOUT',
                    message: 'The waiting time is too long, it will be put on hold in the background.',
                };
            }
            return signalrSellResult;
        });
    }
}
export class Ramp extends BaseRamp {
    constructor() {
        super();
    }
    init(option) {
        return __awaiter(this, void 0, void 0, function* () {
            this.setConfig(option);
            yield this.refreshRampProvider();
        });
    }
    refreshRampProvider() {
        return __awaiter(this, void 0, void 0, function* () {
            const { data: { thirdPart }, } = yield this.service.getRampInfo();
            this.clearProvider();
            Object.keys(thirdPart).forEach(key => {
                switch (key) {
                    case IRampProviderType.AlchemyPay:
                        this.setProvider(new AlchemyPayProvider({
                            providerInfo: Object.assign({ key: IRampProviderType.AlchemyPay }, thirdPart[IRampProviderType.AlchemyPay]),
                            service: new AlchemyPayRampService({
                                fetchRequest: this.fetchRequest,
                            }),
                        }));
                        break;
                    case IRampProviderType.Transak:
                        this.setProvider(new TransakProvider({
                            providerInfo: Object.assign({ key: IRampProviderType.Transak }, thirdPart[IRampProviderType.Transak]),
                            service: new RampService({
                                fetchRequest: this.fetchRequest,
                            }),
                        }));
                        break;
                    default:
                        break;
                }
            });
        });
    }
}
export const ramp = new Ramp();
export * from './api/index.js';
export * from './config/index.js';
export * from './constants/index.js';
export * from './provider/index.js';
export * from './service/index.js';
export * from './signalr/index.js';
export * from './types/index.js';
export * from './utils/index.js';
